import countryCentroids from '../../data/country-centroids.json';

const STORAGE_KEY = 'edfi-country-coordinates-cache';
let persistedCoordinates = {};
let storageLoaded = false;

const ALIASES = {
    UK: 'GB',
    UKM: 'GBR',
    EL: 'GR',
    XKX: 'XK',
    KO: 'XK',
};

const coordinatesIndex = countryCentroids.reduce((acc, entry) => {
    const { cca2, cca3, latlng, capitalLatLng } = entry;

    const primaryLatLng = Array.isArray(latlng) && latlng.length === 2 ? latlng : null;
    const fallbackLatLng = Array.isArray(capitalLatLng) && capitalLatLng.length === 2 ? capitalLatLng : null;
    const source = primaryLatLng || fallbackLatLng;

    if (source) {
        const coordinate = [source[1], source[0]];
        if (cca2) {
            acc[cca2.toUpperCase()] = coordinate;
        }
        if (cca3) {
            acc[cca3.toUpperCase()] = coordinate;
        }
    }

    return acc;
}, {});

function loadPersistedCoordinates() {
    if (storageLoaded) {
        return;
    }

    storageLoaded = true;

    if (typeof window === 'undefined') {
        return;
    }

    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object') {
                persistedCoordinates = parsed;
                Object.entries(parsed).forEach(([key, value]) => {
                    if (Array.isArray(value) && value.length === 2 && !coordinatesIndex[key]) {
                        coordinatesIndex[key] = value;
                    }
                });
            }
        }
    } catch (error) {
        console.warn('[countryCoordinates] Failed to read cached coordinates from localStorage.', error);
    }
}

function savePersistedCoordinates() {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(persistedCoordinates));
    } catch (error) {
        console.warn('[countryCoordinates] Failed to persist coordinates to localStorage.', error);
    }
}

export function getCachedCoordinate(isoCode) {
    if (!isoCode) {
        return null;
    }

    loadPersistedCoordinates();
    const normalizedCode = isoCode.trim().toUpperCase();
    return persistedCoordinates[normalizedCode] || null;
}

export function cacheCoordinate(isoCode, coordinate) {
    if (!isoCode || !Array.isArray(coordinate) || coordinate.length !== 2) {
        return;
    }

    loadPersistedCoordinates();
    const normalizedCode = isoCode.trim().toUpperCase();
    persistedCoordinates[normalizedCode] = coordinate;

    if (!coordinatesIndex[normalizedCode]) {
        coordinatesIndex[normalizedCode] = coordinate;
    }

    savePersistedCoordinates();
}

export function getCountryCoordinates(isoCode) {
    if (!isoCode) {
        return null;
    }

    loadPersistedCoordinates();

    const normalizedCode = isoCode.trim().toUpperCase();
    const aliasTarget = ALIASES[normalizedCode];

    if (coordinatesIndex[normalizedCode]) {
        return coordinatesIndex[normalizedCode];
    }

    if (aliasTarget && coordinatesIndex[aliasTarget]) {
        return coordinatesIndex[aliasTarget];
    }

    return null;
}

export function setCountryCoordinate(isoCode, coordinate) {
    if (!isoCode || !Array.isArray(coordinate) || coordinate.length !== 2) {
        return;
    }

    const normalizedCode = isoCode.trim().toUpperCase();
    coordinatesIndex[normalizedCode] = coordinate;
    cacheCoordinate(normalizedCode, coordinate);
}

export async function fetchCoordinateFromMapbox(isoCode, accessToken) {
    if (!isoCode) {
        return null;
    }

    const normalizedCode = isoCode.trim().toUpperCase();
    const cached = getCachedCoordinate(normalizedCode);

    if (cached) {
        return cached;
    }

    if (!accessToken) {
        return null;
    }

    const query = encodeURIComponent(normalizedCode);
    const endpoints = [`https://api.mapbox.com/search/geocode/v6/forward?q=${query}&types=country&access_token=${accessToken}`, `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?types=country&access_token=${accessToken}`];

    // eslint-disable-next-line no-restricted-syntax
    for (const url of endpoints) {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                // eslint-disable-next-line no-continue
                continue;
            }

            const data = await response.json();
            const feature = data.features && data.features[0];

            if (feature && feature.geometry && Array.isArray(feature.geometry.coordinates)) {
                const coordinate = feature.geometry.coordinates.slice(0, 2);
                setCountryCoordinate(normalizedCode, coordinate);
                return coordinate;
            }
        } catch (error) {
            console.warn(`[countryCoordinates] Failed to fetch coordinates for ISO code ${normalizedCode}`, error);
        }
    }

    return null;
}

export default coordinatesIndex;
